<script setup>
/**
 * Popover — drop-in replacement for BV2's b-popover with triggers="hover".
 *
 * Usage: wrap the trigger element in the default slot; put popover content in #content slot.
 *
 * Props:
 *   customClass — extra class on the popover element
 *   placement   — 'top' | 'bottom' | 'left' | 'right' (default: 'top')
 */
import { ref, nextTick, useSlots } from 'vue';

const slots = useSlots();

const props = defineProps({
    customClass: { type: String, default: '' },
    placement:   { type: String, default: 'top' },
    trigger:     { type: String, default: 'hover' }, // 'hover' | 'click'
});

const show         = ref(false);
const anchor       = ref(null);
const popoverEl    = ref(null);
const popoverStyle = ref({});

const GAP = 12; // px between anchor and popover

async function onEnter() {
    if (props.trigger !== 'hover' || !slots.content) return;
    show.value = true;
    await nextTick();
    positionPopover();
}

function onLeave() {
    if (props.trigger !== 'hover') return;
    show.value = false;
    popoverStyle.value = {};
}

async function onClick() {
    if (props.trigger !== 'click' || !slots.content) return;
    if (show.value) {
        show.value = false;
        popoverStyle.value = {};
    } else {
        positionPopover(); // считаем позицию до рендера — anchor уже в DOM
        show.value = true;
    }
}

function onClickOutside() {
    if (props.trigger !== 'click') return;
    show.value = false;
    popoverStyle.value = {};
}

function positionPopover() {
    if (!anchor.value) return;

    const anchorTarget = anchor.value.firstElementChild || anchor.value;
    const a = anchorTarget.getBoundingClientRect();
    const p = popoverEl.value ? popoverEl.value.getBoundingClientRect() : { width: 0, height: 0 };

    let top, left;

    if (props.placement === 'top') {
        top  = a.top - p.height - GAP;
        left = a.left;
    } else if (props.placement === 'bottom') {
        top  = a.bottom + GAP;
        left = a.left;
    } else if (props.placement === 'left') {
        top  = a.top;
        left = a.left - p.width - GAP;
    } else if (props.placement === 'right') {
        top  = a.top;
        left = a.right + GAP;
    }

    popoverStyle.value = {
        position:   'fixed',
        top:        top + 'px',
        left:       left + 'px',
        zIndex:     1070,
        transform:  'none',
        willChange: 'unset',
        margin:     '0',
    };
}
</script>

<template>
    <span @mouseenter="onEnter" @mouseleave="onLeave" @click.stop="onClick" v-click-outside="onClickOutside">
        <span ref="anchor">
            <slot />
        </span>

        <div
            v-if="show"
            ref="popoverEl"
            role="tooltip"
            :class="['popover', 'b-popover', `bs-popover-${placement}`, customClass]"
            :style="popoverStyle"
        >
            <div class="arrow"></div>
            <div class="popover-body">
                <slot name="content" />
            </div>
        </div>
    </span>
</template>

<style scoped>
.popover {
    text-align: left;
}
</style>
